import api, { ValidateAxiosError } from '@/modules/api.service';
import { AttendanceSheetDayDto, AttendanceSheetResponseDto, CriarFolhaDto, ListAllIntershipsOfTheMonthResponseDto, ListAllMonthsOfAnIntershipResponseDto } from '@/api.types';
import { Endpoints } from '@/shared/constants/endpoints';
import { ListAllIntershipsOfTheMonthFilterDto } from '@/api.types.extra';

export async function createAttendanceSheet(estagioId: number, ano: number, mes: number): Promise<string> {
  const dto: CriarFolhaDto = {
    estagioId: estagioId,
    ano: ano,
    mes: mes
  }

  try {
    return (await api.post(Endpoints.FOLHA_FREQUENCIA, dto)).data;
  } catch (error) {
    return ValidateAxiosError(error, 'Falha ao criar a folha de frequência.');
  }
}

export async function readByIdAttendanceSheet(id: string): Promise<AttendanceSheetResponseDto> {
  try {
    return (await api.get(Endpoints.FOLHA_FREQUENCIA + '/' + id)).data;
  } catch (error) {
    return ValidateAxiosError(error, 'Falha ao buscar a folha de frequência.' + id);
  }
}

export const sendMassNotification = async (
  folhaIds: string[],
  onProgress?: (progresso: number) => void
): Promise<{
  success: string[];
  errors: string[];
  skipped: string[];
}> => {
  const results = {
    success: [] as string[],
    errors: [] as string[],
    skipped: [] as string[]
  };

  const MAX_CONCURRENT = 20;

  for (let i = 0; i < folhaIds.length; i += MAX_CONCURRENT) {
    const batch = folhaIds.slice(i, i + MAX_CONCURRENT);

    const batchResults = await Promise.allSettled(
      batch.map(folhaId =>
        sendNotification(folhaId)
          .then(response => `Folha ${folhaId}: ${response || 'Notificação enviada com sucesso'}`)
          .catch(error => { throw `Folha ${folhaId}: ${error instanceof Error ? error.message : String(error)}`; })
      )
    );

    batchResults.forEach(result => {
      if (result.status === 'fulfilled') {
        results.success.push(result.value);
      } else {
        results.errors.push(result.reason);
      }
    });

    if (onProgress) {
      onProgress(i + batch.length);
    }

    if (i + MAX_CONCURRENT < folhaIds.length) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }

  return results;
};

export async function listAllMonthsOfAnIntership(estagio_id: number): Promise<ListAllMonthsOfAnIntershipResponseDto[]> {
  if (!estagio_id)
    return [];

  try {
    return (
      await api.get(Endpoints.FOLHA_FREQUENCIA + '/listar/' + estagio_id)
    ).data;
  } catch (error) {
    return ValidateAxiosError(error, 'Falha ao buscar as folhas de frequências.');
  }
}

export async function listAllIntershipsOfTheMonth(ano: number, mes: number, dto: ListAllIntershipsOfTheMonthFilterDto): Promise<[ListAllIntershipsOfTheMonthResponseDto[], number]> {
  try {
    const comFolha = (dto as any)?.comFolha;
    const url = Endpoints.FOLHA_FREQUENCIA + `/listar/${ano}/${mes}?unidade=${dto!.unidade}&status=${dto!.status}&vinculo=${dto!.vinculo}&skip=${dto!.skip}&take=${dto!.take}&estagiarioId=${dto!.estagiarioId}&responsavelId=${dto!.responsavelId}&comFolha=${comFolha}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    return ValidateAxiosError(error, 'Falha ao buscar as folhas de frequências.');
  }
}

export async function listPendingAttendanceSheets(
  dto: ListAllIntershipsOfTheMonthFilterDto,
): Promise<[ListAllIntershipsOfTheMonthResponseDto[], number]> {
  try {
    const url = Endpoints.FOLHA_FREQUENCIA + `/pendencias?unidade=${dto!.unidade}&status=${dto!.status}&vinculo=${dto!.vinculo}&skip=${dto!.skip}&take=${dto!.take}&estagiarioId=${dto!.estagiarioId}&responsavelId=${dto!.responsavelId}`;
    return (await api.get(url)).data;
  } catch (error) {
    return ValidateAxiosError(error, 'Falha ao buscar pendências das folhas.');
  }
}

export type ListAllIntershipsOfTheMonthNotificationRow = {
  folhaId: string;
  estagiarioNome: string;
  status: number;
  notificacoesQtd: number;
  ultimaNotificacaoEm: string | null;
};

export async function listAllIntershipsOfTheMonthNotifications(
  ano: number,
  mes: number,
  dto: ListAllIntershipsOfTheMonthFilterDto,
): Promise<ListAllIntershipsOfTheMonthNotificationRow[]> {
  try {
    const url = Endpoints.FOLHA_FREQUENCIA + `/listar/${ano}/${mes}/notificacoes?unidade=${dto!.unidade}&status=${dto!.status}&vinculo=${dto!.vinculo}&skip=${dto!.skip}&take=${dto!.take}&estagiarioId=${dto!.estagiarioId}&responsavelId=${dto!.responsavelId}`;
    return (await api.get(url)).data;
  } catch (error) {
    return ValidateAxiosError(error, 'Falha ao buscar dados de notificações das folhas.');
  }
}

export type NotificationsSummaryRow = {
  folhaId: string;
  notificacoesQtd: number;
  ultimaNotificacaoEm: string | null;
};

export async function notificationsSummaryByIds(
  folhaIds: string[],
): Promise<NotificationsSummaryRow[]> {
  try {
    return (await api.post(`${Endpoints.FOLHA_FREQUENCIA}/notificacoes/resumo`, { folhaIds })).data;
  } catch (error) {
    return ValidateAxiosError(error, 'Falha ao buscar resumo de notificações.');
  }
}

export async function sendToSignatureAttendanceSheet(folhaId: string, aprovado: boolean, justificativa?: string) {
  try {
    const response = await api.patch<string>(`${Endpoints.FOLHA_FREQUENCIA}/assinar/${folhaId}/${aprovado}`, {
      justificativa: justificativa || undefined,
    })
    return response.data;
  } catch (error) {
    return ValidateAxiosError<string>(error, 'Falha ao assinar folha de frequência.');
  }
}

export async function changeResponsible(folhaid: string, novo_responsavel_id?: string) {
  if (!novo_responsavel_id)
    novo_responsavel_id = "0";

  try {
    return (
      await api.patch(`${Endpoints.FOLHA_FREQUENCIA}/${folhaid}/${novo_responsavel_id}`)
    ).data;
  } catch (error) {
    return ValidateAxiosError(error, 'Falha ao assinar folha de frequência.');
  }
}

export async function generateAttendanceSheetsForMonth(year: number, month: number,): Promise<number[]> {
  try {
    return (await api.post(Endpoints.FOLHA_FREQUENCIA + `/${year}/${month}`))
      .data;
  } catch (error) {
    return ValidateAxiosError(error, 'Falha ao gerar folhas de frequências.');
  }
}

export async function fillDayAttendanceSheet(id: string, dias: AttendanceSheetDayDto[]) {
  try {
    await api.put(`${Endpoints.FOLHA_FREQUENCIA}/${id}`, dias);
  } catch (error) {
    return ValidateAxiosError(error, 'Falha ao preencher dias da folha.');
  }
}

export async function sendNotification(folhaId: string) {
  try {
    return (
      await api.post(
        `${Endpoints.FOLHA_FREQUENCIA}/${folhaId}`,
      )
    ).data;
  } catch (error) {
    return ValidateAxiosError(error, 'Falha ao enviar notificação.');
  }
}

export async function deleteAttendanceSheet(folhaId: string): Promise<boolean> {
  try {
    return (await api.delete(`${Endpoints.FOLHA_FREQUENCIA}/${folhaId}`)).data;
  } catch (error) {
    return ValidateAxiosError(error, 'Falha ao excluir folha de frequência.');
  }
}
